import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { translationService } from './translationService';
import { questionKeys } from './keys';
import {
  EditQuestionTranslationCommandInput,
  EditQuestionTranslationCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';

/** Key parameters for identifying a specific translation mutation */
type TranslationMutationKey = {
  questionId: string;
  language: string;
};

/**
 * React Query mutation hook for updating question translations.
 *
 * Features:
 * - Automatic cache invalidation on success
 * - Unique mutation key per question/language to prevent state sharing
 * - Type-safe request/response handling using TypeScript client
 * - Follows feedback mutation pattern
 *
 * @param key - The mutation key parameters (questionId and language) to uniquely identify this mutation
 * @param options - Optional React Query mutation options for callbacks like onSuccess, onError
 * @returns A mutation object with mutate/mutateAsync functions and status flags
 *
 * @example
 * ```tsx
 * const { mutate: updateTranslation, isPending, error } = useUpdateQuestionTranslationMutation(
 *   { questionId: 'q-789', language: 'es' },
 *   {
 *     onSuccess: (data) => {
 *       console.log('Translation version:', data.translationVersion);
 *       toast.success('Translation updated successfully');
 *     },
 *     onError: (error) => {
 *       toast.error(`Failed to update translation: ${error.message}`);
 *     },
 *   }
 * );
 *
 * // Later, trigger the mutation
 * updateTranslation({
 *   programId: 'prog-123',
 *   businessUnit: 'BU-456',
 *   divisionName: 'Division A',
 *   questionId: 'q-789',
 *   language: 'es',
 *   questionTranslation: { ... },
 *   subQuestionTranslations: { ... },
 *   updatedBy: 'user1',
 * });
 * ```
 */
export const useUpdateQuestionTranslationMutation = (
  key: TranslationMutationKey,
  options?: Omit<
    UseMutationOptions<
      EditQuestionTranslationCommandOutput,
      Error,
      EditQuestionTranslationCommandInput,
      unknown
    >,
    'mutationFn' | 'mutationKey'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationKey: questionKeys.updateTranslation(key.questionId, key.language),
    mutationFn: (data: EditQuestionTranslationCommandInput) =>
      translationService.update(data),
    async onSuccess(data, variables, onMutateResult, context) {
      // Invalidate the question detail query to refetch with updated translation
      await queryClient.invalidateQueries({
        queryKey: questionKeys.detail(variables.questionId),
      });

      // Invalidate the specific translation query
      await queryClient.invalidateQueries({
        queryKey: questionKeys.translation(variables.questionId, variables.language),
      });

      // Invalidate all translations for this question
      await queryClient.invalidateQueries({
        queryKey: questionKeys.translations(variables.questionId),
      });

      // Call user's onSuccess callback if provided
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
